import React from "react";
import { PlanogramItem, UnrangedPlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { colorToRGBA } from "src/utils/colorToRgba";
import styled from "styled-components";

import { Color, Flex, Grid } from "src/elements";
import { ProductImage } from "./Image";
import { ResolvedOverlay } from "./overlays/types";
import { OverlayWrapper } from "./OverlayWrapper";
import { shouldRotateFacingImage } from "../../common/planogramFacing";

export { shouldRotateFacingImage } from "../../common/planogramFacing";

const NEW_ITEM_INDICATOR_DIAMETER = 10;
const NEW_ITEM_INDICATOR_INSET = 2;
const NEW_ITEM_INDICATOR_RING_THICKNESS = 2;

const FacingElement = styled(Flex)<{
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  isCapped?: boolean;
}>`
  display: grid;
  position: relative;
  width: ${({ width, scaleX }) => `calc(${width}px * ${scaleX})`};
  height: ${({ height, scaleY }) => `calc(${height}px * ${scaleY})`};
  background-color: ${colorToRGBA(Color.purple100, 1)};
  border-radius: 2px;
  pointer-events: none;
  overflow: hidden;
  box-sizing: border-box;
  border: ${({ isCapped }) => (isCapped ? "1px dashed rgba(0, 0, 0, 0.45)" : "none")};
  opacity: ${({ isCapped }) => (isCapped ? 0.8 : 1)};

  > * {
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  }
`;

const RotatedImageWrapper = styled.div<{ wrapperW: number; wrapperH: number }>`
  position: absolute;
  width: ${({ wrapperW }) => `${wrapperW}px`};
  height: ${({ wrapperH }) => `${wrapperH}px`};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(90deg);
`;

const GridCell = styled(Flex)`
  position: relative;
  overflow: hidden;
`;

const NewItemDot = styled.div<{
  diameter?: number;
  inset?: number;
  ringThickness?: number;
}>`
  position: absolute;
  top: ${({ inset = NEW_ITEM_INDICATOR_INSET }) => `${inset}px`};
  left: ${({ inset = NEW_ITEM_INDICATOR_INSET }) => `${inset}px`};
  width: ${({ diameter = NEW_ITEM_INDICATOR_DIAMETER }) => `${diameter}px`};
  height: ${({ diameter = NEW_ITEM_INDICATOR_DIAMETER }) => `${diameter}px`};
  border-radius: 50%;
  border: ${({ ringThickness = NEW_ITEM_INDICATOR_RING_THICKNESS }) =>
    `${ringThickness}px solid ${Color.white}`};
  background: ${Color.primary};
  box-shadow: 0 1px 2px ${colorToRGBA(Color.black, 0.25)};
  pointer-events: none;
  z-index: 11;
`;

interface Props {
  item: PlanogramItem | UnrangedPlanogramItem;
  scaleX: number;
  scaleY: number;
  showImage: boolean;
  overrideOrientation?: PlanogramItem["orientation"];
  overrideWidth?: number;
  overrideHeight?: number;
  isCapped?: boolean;
  showNewLabel?: boolean;
  overlays?: ResolvedOverlay[];
  isCustomItem?: boolean;
}

const FacingInner = ({
  item,
  scaleX,
  scaleY,
  showImage,
  overrideOrientation,
  overrideWidth,
  overrideHeight,
  isCapped,
  showNewLabel = false,
  overlays = [],
  isCustomItem = false,
}: Props) => {
  const isOverride =
    overrideOrientation !== undefined ||
    overrideWidth !== undefined ||
    overrideHeight !== undefined;

  const orientation = overrideOrientation ?? item.orientation;
  const width = overrideWidth ?? item.getWidth();
  const height = overrideHeight ?? item.getHeight();

  const unitsWide = isOverride ? 1 : item.getUnitsWide();
  const unitsHigh = isOverride ? 1 : item.getUnitsHigh();

  const shouldRotateImage = shouldRotateFacingImage(orientation);
  const cellW = (width * scaleX) / unitsWide;
  const cellH = (height * scaleY) / unitsHigh;

  return (
    <FacingElement
      width={width}
      height={height}
      scaleX={scaleX}
      scaleY={scaleY}
      isCapped={isCapped}
    >
      {showImage &&
        (shouldRotateImage ? (
          // Rotated artwork cannot tile with a repeating background.
          <Grid columns={`repeat(${unitsWide}, 1fr)`} rows={`repeat(${unitsHigh}, 1fr)`}>
            {Array.from(Array(unitsWide * unitsHigh)).map((_row, i) => (
              <GridCell key={i}>
                <RotatedImageWrapper wrapperW={cellH} wrapperH={cellW}>
                  <ProductImage productCode={item.productCode} size="small" />
                </RotatedImageWrapper>
              </GridCell>
            ))}
          </Grid>
        ) : (
          <ProductImage
            productCode={item.productCode}
            size="small"
            tileX={unitsWide}
            tileY={unitsHigh}
          />
        ))}
      {overlays
        .filter((o) => o.showOnImage || (isCustomItem && !showImage && o.showOnLabelPlaceholder))
        .map((o) => (
          <OverlayWrapper key={o.id} $anchor={o.anchor} style={o.style}>
            {o.content}
          </OverlayWrapper>
        ))}
      {showNewLabel && <NewItemDot role="img" aria-label="New item indicator" />}
    </FacingElement>
  );
};

export const Facing = React.memo(FacingInner);
